import styled from "@emotion/styled";
import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@tanstack/react-query-devtools/build/lib/theme";
import {
  ColDef,
  ICellRendererParams,
  SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useContext, useEffect, useRef, useState } from "react";
import { customTheme } from "../../../../config/theme/MuiTheme";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import EmptyStateGrid from "../common/EmptyStateGrid";
import GenericFilterBox from "../common/GenericFilterBox";
import { nameof } from "../common/ObjectUtils";
import { DriversTypes } from "./Types";
import AddIcon from "@mui/icons-material/Add";
import useToggle from "../../hooks/useToggle";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import { ContextOperation } from "../../context/OperationContext";
import useTenant from "../../hooks/useTenant";
import React from "react";
import { Box, Tooltip } from "@mui/material";
import DriversServer from "./service/drivers.action";
import EditDriver from "./EditDriver";
import dayjs from "dayjs";
import CreateDriver from "./CreateDriver";
import { useForm } from "react-hook-form";
import FormInput from "../common/FormInput";
import { useHasScope } from "../../../../config/globalConfig";
import AgGridOperationTable from "../common/AgGridOperationTable";

import FileSaver from "file-saver";
const StyledActionsBox = styled(Mui.Box)({
  backgroundColor: customTheme.palette.neutral.bright,
});

export const DriversTable = (props: any) => {
  const hasScope = useHasScope;
  const gridEquipmentKitsRef = useRef<AgGridReact>(null);
  const gridEquipmentsRef = useRef<AgGridReact>(null);
  const t = useOperationTranslation();
  const [showEdit, toggleEdit] = useToggle();
  const [showCreate, toggleCreate] = useToggle();
  const [idForDelete, setIdForDelete] = useState<any>({});

  const [driver, setDriver] = useState<any | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const { setLoading } = useContext(ContextOperation);
  const { tenant } = useContext(ContextOperation);
  const [open, setOpen] = React.useState(false);
  const onClickCloseDialog = () => setOpen(false);
  const [refreshTableByDelete, setRefreshTableByDelete] = useState(false);
  const form = useForm<any>({
    defaultValues: {
      deleted: false,
    },
  });
  const { tenantOptions: options } = useTenant();
  const [damagePriceListDataSource, totalDrivers, updateDatasourceDamage] =
    useAgGridCustomDatasource(
      DriversServer.get.getAllDrivers,
      [tenant, form.getValues()],
      [tenant, form.formState.submitCount]
    );
  const snackbar = useSnackbar();

  const statusArray = [
    { label: t("internOptionSelectName.yes"), value: true },
    { label: t("internOptionSelectName.no"), value: false },
  ];

  useEffect(() => {
    gridEquipmentKitsRef.current?.api.deselectAll();
  }, [form.formState.submitCount]);

  const openEditModal = (params: any) => {
    setDriver(params.data);
    toggleEdit();
  };
  const onSubmit = () => {};

  const handleDownloadTemplate = () => {
    setLoading(true);
    DriversServer.get
      .export(tenant, form.getValues())
      .then((response) => {
        const filename =
          response.headers["content-disposition"].split("filename=")[1];
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, filename);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        snackbar.show(
          "error",
          t("vehicleEquipment.modifyEquipment.actionError")
        );
      });
  };

  //   setLoading(true);
  //   DamageService.get.exportExel(formik.values, tenant).then((response) => {
  //     const filename =
  //       response.headers["content-disposition"].split("filename=")[1];
  //     var blob = new Blob([response.data], {
  //       type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //     });
  //     FileSaver.saveAs(blob, filename);
  //     setLoading(false);
  //   });

  // };

  const deleteSelectedPrice = () => {
    setLoading(true);
    DriversServer.delete
      .deleteDrivers(idForDelete, tenant)
      .then(() => {
        setLoading(false);
        updateDatasourceDamage();
        snackbar.show("success", t("drivers.delete.successMessage"));
        setOpen(false);
      })
      .catch((error: any) => {
        if (error.response.status == 409) {
          snackbar.show("error", t("drivers.delete.delete409Error"));
        }
        setLoading(false);
      });
    setOpen(false);
  };

  const onClickDelete = (params: any) => {
    setIdForDelete(params.data.id);
    setOpen(true);
  };

  const isDisabled = () => {
    if (Object.keys(form?.getValues).length !== 1 || options.length > 1) {
      return Object.values(form?.getValues()).every(
        (x) => x === null || x === undefined || x === ""
      );
    }
    return true;
  };

  const pinnedFunction = () => {
    if (
      hasScope("SCOPE_UPDATE_MDTRANSPORTATION_DRIVER") ||
      hasScope("SCOPE_CREATE_MDTRANSPORTATION_DRIVER") ||
      hasScope("SCOPE_DELETE_MDTRANSPORTATION_DRIVER")
    )
      return "right";
    else return null;
  };

  const widthFunction = () => {
    if (
      hasScope("SCOPE_UPDATE_MDTRANSPORTATION_DRIVER") ||
      hasScope("SCOPE_CREATE_MDTRANSPORTATION_DRIVER") ||
      hasScope("SCOPE_DELETE_MDTRANSPORTATION_DRIVER")
    )
      return 100;
    else return 0;
  };

  const driversColumns: ColDef[] = [
    {
      field: nameof<DriversTypes.Drivers>("surname"),
      headerName: t("drivers.fields.surname"),
      minWidth: 120,
    },
    {
      field: nameof<DriversTypes.Drivers>("name"),
      headerName: t("drivers.fields.name"),
      minWidth: 120,
    },
    {
      field: nameof<DriversTypes.Drivers>("fiscalCode"),
      headerName: t("drivers.fields.fiscalCode"),
      minWidth: 220,
    },

    {
      field: nameof<DriversTypes.Drivers>("birthDate"),
      headerName: t("drivers.fields.birthDate"),
      minWidth: 220,
      cellRenderer: (params: any) => {
        return <>{dayjs(`${params.data.birthDate}`).format("DD/MM/YYYY")}</>;
      },
    },
    {
      field: nameof<DriversTypes.Drivers>("address"),
      headerName: t("drivers.fields.address"),
      minWidth: 220,
    },
    {
      field: nameof<DriversTypes.Drivers>("birthNation"),
      headerName: t("drivers.fields.birthNation"),
      minWidth: 220,
    },

    {
      field: nameof<DriversTypes.Drivers>("residenceAddress"),
      headerName: t("drivers.fields.residenceAddress"),
      minWidth: 250,
    },
    {
      field: nameof<DriversTypes.Drivers>("countryResidence"),
      headerName: t("drivers.fields.countryResidence"),
      minWidth: 220,
    },

    {
      field: nameof<DriversTypes.Drivers>("drivingLicenseNumber"),
      headerName: t("drivers.fields.drivingLicense"),
      minWidth: 220,
    },
    {
      field: nameof<DriversTypes.Drivers>("mobile"),
      headerName: t("drivers.fields.mobile"),
      minWidth: 220,
    },
    {
      field: nameof<DriversTypes.Drivers>("deleted"),
      headerName: t("drivers.fields.delete"),
      minWidth: 160,
      cellRenderer: (props: ICellRendererParams<DriversTypes.Drivers>) => {
        return props.data?.deleteDate
          ? t("internOptionSelectName.yes")
          : t("internOptionSelectName.no");
      },
    },

    {
      maxWidth: widthFunction(),
      minWidth: widthFunction(),
      pinned: pinnedFunction(),
      cellStyle: {
        padding: "0px",
        border: "none",
      },
      cellRenderer: (props: ICellRendererParams<DriversTypes.Drivers>) => {
        const onClick = () => {
          setDriver(props.data);
        };
        return (
          <StyledActionsBox>
            {hasScope("SCOPE_UPDATE_MDTRANSPORTATION_DRIVER") && (
              <Mui.Tooltip title={t("tooltipTitle.edit")}>
                <Mui.IconButton
                  color="accent"
                  disabled={props.data?.deleteDate === undefined ? false : true}
                >
                  <MuiIcons.Edit onClick={() => openEditModal(props)} />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}
            {hasScope("SCOPE_DELETE_MDTRANSPORTATION_DRIVER") && (
              <Tooltip title={t("drivers.fields.deleteTooltip")}>
                <Mui.IconButton
                  color="accent"
                  disabled={props.data?.deleteDate === undefined ? false : true}
                  onClick={() => onClickDelete(props)}
                >
                  <MuiIcons.Delete />
                </Mui.IconButton>
              </Tooltip>
            )}
          </StyledActionsBox>
        );
      },
    },
  ];

  return (
    <Mui.Box>
      {showCreate === false ? (
        <>
          <form
            id="equipment-kit-tab-search-form"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <GenericFilterBox
              formId="equipment-kit-tab-search-form"
              disableResetButton={isDisabled()}
              onReset={() => {
                form.reset();
              }}
            >
              <FormInput
                type="text"
                label={t("drivers.fields.surname")}
                name={nameof<DriversTypes.Drivers>("surname")}
                formInstance={form}
                fullWidth
              />
              <FormInput
                type="text"
                label={t("drivers.fields.name")}
                name={nameof<DriversTypes.Drivers>("name")}
                formInstance={form}
                fullWidth
              />

              <FormInput
                type="select"
                options={statusArray}
                label={t("drivers.fields.delete")}
                name={nameof<DriversTypes.Drivers>("deleted")}
                value={form.watch("deleted")}
                onChange={(value) => {
                  form.setValue(nameof<DriversTypes.Drivers>("deleted"), value);
                }}
                fullWidth
              />
            </GenericFilterBox>
          </form>

          <Mui.Card>
            <Mui.CardContent>
              <Mui.Box
                sx={{
                  backgroundColor: "white",
                  border: " 1px solid #dbe4e9",
                  borderRadius: "2px",
                  padding: "9px 1px 3px 2px",
                }}
              >
                <Mui.Grid
                  container
                  spacing={2}
                  alignItems="center"
                  alignContent="center"
                  direction="row"
                >
                  <Mui.Grid item xs={6}></Mui.Grid>
                  <Mui.Grid item xs={6}>
                    <Mui.Box
                      sx={{
                        justifyContent: "flex-end",
                        display: "flex",
                        gap: "10px",
                      }}
                    >
                      <Mui.Stack direction="row" alignItems="center">
                        <Mui.IconButton
                          disabled={totalDrivers ? false : true}
                          onClick={handleDownloadTemplate}
                          color="accent"
                        >
                          <Mui.Tooltip title={t("tooltipTitle.download")}>
                            <FileDownloadIcon />
                          </Mui.Tooltip>
                        </Mui.IconButton>
                        {hasScope("SCOPE_CREATE_MDTRANSPORTATION_DRIVER") && (
                          <Mui.IconButton color="accent">
                            <Mui.Tooltip title={t("tooltipTitle.add")}>
                              <AddIcon onClick={() => toggleCreate()} />
                            </Mui.Tooltip>
                          </Mui.IconButton>
                        )}
                      </Mui.Stack>
                    </Mui.Box>
                  </Mui.Grid>
                </Mui.Grid>
              </Mui.Box>

              <Mui.Grid>
                <Mui.Grid item sx={{ whiteSpace: "pre-wrap" }}>
                  <AgGridOperationTable
                    rowStyle={{ width: "250%" }}
                    gridRef={gridEquipmentsRef}
                    columnDefs={driversColumns}
                    serverSideDatasource={damagePriceListDataSource}
                    rowModelType="serverSide"
                    serverSideInfiniteScroll={true}
                    cacheBlockSize={10}
                    rowSelection="single"
                    totalElements={totalDrivers}
                    emptyState={{
                      title: t("drivers.noData.title"),
                      content: t("drivers.noData.body")
                    }}
                  />

                </Mui.Grid>
              </Mui.Grid>
            </Mui.CardContent>
          </Mui.Card>
        </>
      ) : (
        <CreateDriver
          open={showCreate}
          onClose={toggleCreate}
          updateDatasourceDamage={updateDatasourceDamage}
        />
      )}

      <EditDriver
        open={showEdit}
        onClose={toggleEdit}
        updateDatasourceDamage={updateDatasourceDamage}
        driver={driver}
      />

      <Mui.Dialog
        open={open}
        PaperProps={{
          sx: {
            minWidth: "600px !important",
            minHeight: "194px",
            borderRadius: "4px",
          },
        }}
      >
        <Mui.DialogTitle>
          <Mui.Grid
            container
            alignItems="center"
            alignContent="center"
            direction="row"
          >
            <Mui.Grid xs item>
              <Mui.Stack direction="row" alignItems="center" gap={1}>
                <MuiIcons.Info sx={{ color: "#C8017D" }} fontSize="small" />
                <Box>{t("drivers.delete.title")}</Box>
              </Mui.Stack>
            </Mui.Grid>
            <Mui.Grid
              xs
              item
              sx={{
                justifyContent: "flex-end",
                display: "flex",
              }}
            >
              <MuiIcons.Close
                color="disabled"
                fontSize="small"
                onClick={onClickCloseDialog}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogTitle>

        <Mui.DialogContent>
          <Mui.DialogContentText>
            {t("drivers.delete.bodyMessages")}
          </Mui.DialogContentText>
        </Mui.DialogContent>
        <Mui.DialogActions>
          <Mui.Stack direction="row" spacing={2} p={1} mr={1}>
            <Mui.Button
              sx={{
                maxWidth: "111px",
                maxHeight: "48px",
                minWidth: "111px",
                minHeight: "48px",
              }}
              variant="outlined"
              onClick={onClickCloseDialog}
            >
              <Mui.Typography
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 700,
                  lineHeight: 2.25,
                  color: "#C8017D",
                }}
                variant="subtitle1"
              >
                {t("actionButtons.cancel")}
              </Mui.Typography>
            </Mui.Button>

            <Mui.Button
              sx={{
                maxWidth: "111px",
                maxHeight: "48px",
                minWidth: "111px",
                minHeight: "48px",
              }}
              variant="contained"
              color="accent"
              onClick={deleteSelectedPrice}
            >
              <Mui.Typography
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 700,
                  lineHeight: 2.25,
                  color: "white",
                }}
                variant="subtitle1"
              >
                {t("actionButtons.confirm")}
              </Mui.Typography>
            </Mui.Button>
          </Mui.Stack>
        </Mui.DialogActions>
      </Mui.Dialog>
    </Mui.Box>
  );
};
